const SITE_URL = 'https://cashboomerang.store'
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`

export const defaultSeo = {
  title: 'CashBoomerang | Cash Back Guides and Curated Shopping Deals',
  description: 'Learn how online cash back works, compare eligible shopping offers, and browse clearly disclosed affiliate deals with CashBoomerang.',
  canonical: '/',
  robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
}

function setMeta(selector, attributes) {
  let element = document.head.querySelector(selector)
  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }
  Object.keys(attributes).forEach(key => element.setAttribute(key, attributes[key]))
}

function setLink(selector, attributes) {
  let element = document.head.querySelector(selector)
  if (!element) {
    element = document.createElement('link')
    document.head.appendChild(element)
  }
  Object.keys(attributes).forEach(key => element.setAttribute(key, attributes[key]))
}

function resolveSeo(route) {
  const matched = route.matched.slice().reverse().find(record => record.meta && record.meta.seo)
  return { ...defaultSeo, ...(matched ? matched.meta.seo : {}) }
}

export function applyRouteSeo(route) {
  const seo = resolveSeo(route)
  const canonicalPath = seo.canonical || route.path || '/'
  const canonicalUrl = canonicalPath.startsWith('http') ? canonicalPath : `${SITE_URL}${canonicalPath}`
  const imageUrl = seo.image || DEFAULT_IMAGE

  document.title = seo.title
  setMeta('meta[name="description"]', { name: 'description', content: seo.description })
  setMeta('meta[name="robots"]', { name: 'robots', content: seo.robots })
  setMeta('meta[property="og:type"]', { property: 'og:type', content: seo.type || 'website' })
  setMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'CashBoomerang' })
  setMeta('meta[property="og:title"]', { property: 'og:title', content: seo.title })
  setMeta('meta[property="og:description"]', { property: 'og:description', content: seo.description })
  setMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl })
  setMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl })
  setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
  setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: seo.title })
  setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: seo.description })
  setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl })
  setLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl })

  document.documentElement.lang = seo.lang || 'en'
  replaceStructuredData(seo.structuredData || buildBreadcrumbData(route, seo.title, canonicalUrl))
}

export function applyProductSeo(product, category) {
  if (!product) return
  const title = `${product.titulo} Shopping Guide | CashBoomerang`
  const description = String(product.ai_one_liner || product.ai_why_love || `View product details and shopping information for ${product.titulo}.`)
    .replace(/\s+/g, ' ')
    .slice(0, 158)
  const canonicalUrl = `${SITE_URL}/product/${encodeURIComponent(category)}/${encodeURIComponent(product.id)}`

  document.title = title
  setMeta('meta[name="description"]', { name: 'description', content: description })
  setMeta('meta[name="robots"]', { name: 'robots', content: 'noindex,follow' })
  setMeta('meta[property="og:title"]', { property: 'og:title', content: title })
  setMeta('meta[property="og:description"]', { property: 'og:description', content: description })
  setMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl })
  setMeta('meta[property="og:image"]', { property: 'og:image', content: product.imagen || DEFAULT_IMAGE })
  setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title })
  setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description })
  setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: product.imagen || DEFAULT_IMAGE })
  setLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl })
}

function buildBreadcrumbData(route, title, canonicalUrl) {
  if (!route.path || route.path === '/') return null
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: title.split('|')[0].trim(), item: canonicalUrl }
    ]
  }
}

function replaceStructuredData(data) {
  document.querySelectorAll('script[data-route-structured-data]').forEach(node => node.remove())
  if (!data) return

  const items = Array.isArray(data) ? data : [data]
  items.forEach(item => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.dataset.routeStructuredData = 'true'
    script.textContent = JSON.stringify(item)
    document.head.appendChild(script)
  })
}

